import React, { useEffect, useState } from 'react';
import api from '../services/api';
import Modal from './Modal';
import { useAuth } from '../context/AuthContext';
import { formatMonto } from '../utils/format';
import { tipoPersona, valoresIslr, TIPO_PERSONA_LABEL, retencionesConfiguradas } from '../utils/retenciones';

// Botón "Retenciones" del proveedor (casos 5 y 6 de Armando): qué se le
// retiene en cada factura de compra. Las facturas nuevas lo traen puesto y
// calculado; en la factura se puede ajustar el % si hace falta.
// El % de ISLR sale del concepto según el tipo de persona (letra del RIF).
export default function RetencionesProveedor({ proveedor, onGuardado, onCerrar }) {
  const { empresaActivaData } = useAuth();
  const [conceptos, setConceptos] = useState([]);
  const configurado = retencionesConfiguradas(proveedor);
  const [form, setForm] = useState({
    retencion_iva_pct: configurado ? String(proveedor.retencion_iva_pct) : '75',
    retencion_islr_codigo_id: configurado ? String(proveedor.retencion_islr_codigo_id || 0) : '0',
    retencion_municipal: configurado ? !!proveedor.retencion_municipal : true
  });
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);
  const tipo = tipoPersona(proveedor.rif);

  useEffect(() => {
    api.get('/retencion-islr-codigos').then(({ data }) => setConceptos(data)).catch(() => {});
  }, []);

  const elegido = conceptos.find((c) => String(c.id) === form.retencion_islr_codigo_id);
  const valores = valoresIslr(elegido, tipo);

  async function guardar(e) {
    e.preventDefault();
    setError('');
    setGuardando(true);
    try {
      const { data } = await api.put(`/proveedores/${proveedor.id}/retenciones`, {
        retencion_iva_pct: Number(form.retencion_iva_pct),
        retencion_islr_codigo_id: Number(form.retencion_islr_codigo_id) || 0,
        retencion_municipal: form.retencion_municipal
      });
      onGuardado(data);
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudieron guardar las retenciones');
      setGuardando(false);
    }
  }

  const empresa = empresaActivaData || {};
  const ningunaAplica = !empresa.agente_retencion && !empresa.agente_retencion_islr && !empresa.agente_retencion_municipal;

  return (
    <Modal title={`Retenciones · ${proveedor.nombre}`} onClose={onCerrar} maxWidth="max-w-lg">
      <form onSubmit={guardar} className="space-y-4">
        <p className="rounded-md bg-slate-50 px-3 py-2 text-sm text-slate-600">
          {TIPO_PERSONA_LABEL[tipo]}
          <span className="text-slate-400"> · {proveedor.rif ? `RIF ${proveedor.rif}` : 'sin RIF (se toma como jurídica)'}</span>
        </p>
        {ningunaAplica && (
          <p className="text-sm text-amber-700">
            Esta empresa no está marcada como agente de retención (Contabilidad → Configuración contable), así que sus facturas no retienen nada.
          </p>
        )}

        {!!empresa.agente_retencion && (
          <div>
            <label className="block text-xs text-slate-500 mb-1">IVA</label>
            <select
              value={form.retencion_iva_pct}
              onChange={(e) => setForm({ ...form, retencion_iva_pct: e.target.value })}
              className="border rounded-md px-3 py-2 w-full text-sm"
            >
              <option value="75">Retiene 75 %</option>
              <option value="100">Retiene 100 %</option>
              <option value="0">No se le retiene IVA</option>
            </select>
          </div>
        )}

        {!!empresa.agente_retencion_islr && (
          <div>
            <label className="block text-xs text-slate-500 mb-1">ISLR</label>
            <select
              value={form.retencion_islr_codigo_id}
              onChange={(e) => setForm({ ...form, retencion_islr_codigo_id: e.target.value })}
              className="border rounded-md px-3 py-2 w-full text-sm"
            >
              <option value="0">No se le retiene ISLR</option>
              {conceptos.filter((c) => c.activo || String(c.id) === form.retencion_islr_codigo_id).map((c) => (
                <option key={c.id} value={c.id}>{c.codigo} · {c.concepto}</option>
              ))}
            </select>
            {elegido && (
              <p className={`mt-1 text-xs ${valores.porcentaje == null ? 'text-amber-700' : 'text-slate-500'}`}>
                {valores.porcentaje == null
                  ? `Este concepto todavía no tiene % de ${TIPO_PERSONA_LABEL[tipo].toLowerCase()}: cárgalo en Contabilidad → Configuración contable.`
                  : `${TIPO_PERSONA_LABEL[tipo]}: ${formatMonto(valores.porcentaje)} %${Number(valores.sustraendo) ? ` − sustraendo ${formatMonto(valores.sustraendo)}` : ''}`}
              </p>
            )}
          </div>
        )}

        {!!empresa.agente_retencion_municipal && (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={form.retencion_municipal}
              onChange={(e) => setForm({ ...form, retencion_municipal: e.target.checked })}
              className="w-4 h-4 accent-primary-600"
            />
            Retiene municipal ({formatMonto(empresa.retencion_municipal_pct ?? 2)} %)
          </label>
        )}

        {error && <p className="text-red-600 text-sm">{error}</p>}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onCerrar} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100">
            Cancelar
          </button>
          <button type="submit" disabled={guardando} className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm hover:bg-primary-700 disabled:opacity-60">
            Guardar
          </button>
        </div>
      </form>
    </Modal>
  );
}
